import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { getIcon } from "@/hooks/use-categories";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Plus,
  ChevronRight,
  FileText,
  Key,
  User,
  Users,
  Loader2,
  GripVertical,
  Trash2,
  Pencil,
  Check,
  X,
} from "lucide-react";
import { Link } from "wouter";
import type { GenericCategory } from "@shared/schema";
import { AddCategoryDialog } from "@/components/AddCategoryDialog";
import { DeleteConfirmDialog } from "@/components/DeleteConfirmDialog";

const CATEGORY_TYPE_LABELS: Record<string, { label: string; icon: typeof FileText }> = {
  checklist: { label: "Checklist", icon: FileText },
  passwords: { label: "Password Log", icon: Key },
  personal_info: { label: "Personal Info", icon: User },
  dependents: { label: "Dependants", icon: Users },
};

interface SortableCategoryRowProps {
  category: GenericCategory;
  onDelete: (category: GenericCategory) => void;
}

function SortableCategoryRow({ category, onDelete }: SortableCategoryRowProps) {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(category.name);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const renameMutation = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("PATCH", `/api/admin/field-config/categories/${category.id}`, { name });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/generic-categories"] });
      toast({ title: "Category renamed", description: `Category renamed to "${editName.trim()}".` });
      setIsEditing(false);
    },
    onError: (error: Error) => {
      toast({ title: "Error renaming category", description: error.message, variant: "destructive" });
    },
  });

  const startEditing = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setEditName(category.name);
    setIsEditing(true);
  };

  const confirmRename = () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === category.name) {
      setIsEditing(false);
      return;
    }
    renameMutation.mutate(trimmed);
  };

  const cancelRename = () => {
    setEditName(category.name);
    setIsEditing(false);
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const Icon = getIcon(category.icon);
  const typeInfo = CATEGORY_TYPE_LABELS[category.categoryType] || { label: category.categoryType, icon: FileText };
  const TypeIcon = typeInfo.icon;
  const isSystem = category.isSystem;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 ${isDragging ? "opacity-50" : ""}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab p-1 text-muted-foreground hover:text-foreground"
        data-testid={`drag-handle-${category.id}`}
      >
        <GripVertical className="h-5 w-5" />
      </div>

      <Link
        href={`/categories/${category.slug}`}
        className="flex-1 block"
      >
        <div
          className="flex items-center gap-4 p-4 rounded-lg border hover-elevate cursor-pointer transition-colors"
          data-testid={`category-row-${category.id}`}
        >
          <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-primary/10">
            <Icon className="h-5 w-5 text-primary" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              {isEditing ? (
                <div className="flex items-center gap-1" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                  <Input
                    ref={inputRef}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") confirmRename();
                      if (e.key === "Escape") cancelRename();
                    }}
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    className="h-7 text-sm w-48"
                    disabled={renameMutation.isPending}
                    data-testid={`input-rename-category-${category.id}`}
                  />
                  <Button aria-label="Confirm rename"
                    size="icon"
                    variant="ghost"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); confirmRename(); }}
                    disabled={!editName.trim() || renameMutation.isPending}
                    data-testid={`button-confirm-rename-category-${category.id}`}
                  >
                    {renameMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3 text-green-600" />}
                  </Button>
                  <Button aria-label="Cancel rename"
                    size="icon"
                    variant="ghost"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); cancelRename(); }}
                    disabled={renameMutation.isPending}
                    data-testid={`button-cancel-rename-category-${category.id}`}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ) : (
                <>
                  <span className="font-medium" data-testid={`text-category-name-${category.id}`}>
                    {category.name}
                  </span>
                  {!isSystem && (
                    <Button aria-label="Rename category"
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6"
                      onClick={startEditing}
                      data-testid={`button-rename-category-${category.id}`}
                    >
                      <Pencil className="h-3 w-3 text-muted-foreground" />
                    </Button>
                  )}
                </>
              )}
              <Badge variant="secondary" className="gap-1">
                <TypeIcon className="h-3 w-3" />
                {typeInfo.label}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">
              Slug: {category.slug}
            </p>
          </div>

          <ChevronRight className="h-5 w-5 text-muted-foreground" />
        </div>
      </Link>

      <Button aria-label="Delete category"
        variant="ghost"
        size="icon"
        className="text-muted-foreground hover:text-destructive"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDelete(category);
        }}
        data-testid={`delete-category-${category.id}`}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

export default function UnifiedCategories() {
  const { toast } = useToast();
  const [addCategoryOpen, setAddCategoryOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<GenericCategory | null>(null);

  const { data: categories = [], isLoading } = useQuery<GenericCategory[]>({
    queryKey: ["/api/generic-categories", { clientId: "null" }],
    queryFn: async () => {
      const res = await fetch("/api/generic-categories?clientId=null");
      if (!res.ok) throw new Error("Failed to fetch categories");
      return res.json();
    },
  });

  const sortedCategories = [...categories]
    .filter(c => c.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const reorderMutation = useMutation({
    mutationFn: async (updates: { id: string; sortOrder: number }[]) => {
      await apiRequest("PUT", "/api/generic-categories/reorder", { updates });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/generic-categories"] });
      toast({
        title: "Categories reordered",
        description: "The category order has been updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error reordering categories",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const addCategoryMutation = useMutation({
    mutationFn: async (data: { title: string; icon: string }) => {
      const slug = data.title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
      const maxSortOrder = Math.max(0, ...sortedCategories.map(c => c.sortOrder));
      const res = await apiRequest("POST", "/api/generic-categories", {
        name: data.title,
        slug,
        icon: data.icon,
        categoryType: "checklist",
        isDefaultTemplate: true,
        isActive: true,
        clientId: null,
        sortOrder: maxSortOrder + 1,
      });
      return await res.json();
    },
    onSuccess: (_, data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/generic-categories"] });
      toast({
        title: "Category added",
        description: `"${data.title}" has been created.`,
      });
      setAddCategoryOpen(false);
    },
    onError: (error: Error) => {
      toast({
        title: "Error adding category",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/generic-categories/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/generic-categories"] });
      queryClient.invalidateQueries({ queryKey: ["/api/unified/progress"] });
      toast({
        title: "Category deleted",
        description: "The category has been removed.",
      });
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting category",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sortedCategories.findIndex((cat) => cat.id === active.id);
      const newIndex = sortedCategories.findIndex((cat) => cat.id === over.id);
      const newOrder = arrayMove(sortedCategories, oldIndex, newIndex);
      
      const updates = newOrder.map((category, index) => ({
        id: category.id,
        sortOrder: index,
      }));
      
      reorderMutation.mutate(updates);
    }
  };

  const handleDeleteClick = (category: GenericCategory) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (categoryToDelete) {
      deleteCategoryMutation.mutate(categoryToDelete.id);
    }
  };

  // SINGLE-FLIGHT-PRIMITIVE-1 (s170): each fire mints a category row.
  const addCategoryFlight = useSingleFlight();

  const handleAddCategory = (data: { title: string; icon: string }) => {
    addCategoryFlight.runQuiet(() => addCategoryMutation.mutateAsync(data));
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>	
          <h1 className="text-2xl font-bold" data-testid="text-page-title">Default Categories</h1>
          <p className="text-muted-foreground">
            Manage the template categories that are available to all clients. All content types are now unified.
          </p>
        </div>
        <Button onClick={() => setAddCategoryOpen(true)} data-testid="button-add-category">
          <Plus className="h-4 w-4 mr-2" />
          Add Category
        </Button>
      </div>


      <Card>
        <CardHeader>
          <CardTitle>All Categories</CardTitle>
          <CardDescription>
            Template categories define the structure and default content for client estate planning.
            Click a category to view and manage its sections and fields. Drag to reorder.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : sortedCategories.length === 0 ? (
            <div className="text-center py-8">
              <div className="flex justify-center mb-3">
                <div className="rounded-full bg-muted p-3">
                  <FileText className="h-6 w-6 text-muted-foreground" />
                </div>
              </div>
              <p className="font-medium">No categories defined yet</p>
              <p className="text-sm text-muted-foreground mt-1">Template categories define the structure available to all clients.</p>
              <Button variant="outline" className="mt-4 gap-2" onClick={() => setAddCategoryOpen(true)}>
                <Plus className="h-4 w-4 mr-1" />
                Add Category
              </Button>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sortedCategories.map(c => c.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {sortedCategories.map((category) => (
                    <SortableCategoryRow
                      key={category.id}
                      category={category}
                      onDelete={handleDeleteClick}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </CardContent>
      </Card>

      <AddCategoryDialog
        open={addCategoryOpen}
        onOpenChange={setAddCategoryOpen}
        onSubmit={handleAddCategory}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Category"
        description={`Are you sure you want to delete "${categoryToDelete?.name}"? This will permanently remove the category and all its sections and fields. This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        isPending={deleteCategoryMutation.isPending}
        testIdPrefix="category"
      />
    </div>
  );
}
